import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { EmptyState, PageHeader, Pagination, SearchInput, Toolbar } from "@/components/DataTableParts";
import { Loader2, Plus, Pencil, Play, Send, Trash2 } from "lucide-react";
import { canProcure, canDeleteRecords, useAuth } from "@/lib/auth";

// V1.7 — Purchases list uses a single derived "Workflow Status" per row that
// is friendlier than the raw DB enum. The Progress column is computed from
// the aggregated laptop_passport counts returned by the API.

// V2.9.4 — Compact per-PO product summary shown as a small grey caption
// under each PO row. Purely visual — fetches the existing
// GET /api/purchases/{id} for its `lines` and buckets them into a friendly
// abbreviation. No backend or API contract changes.
const ITEM_ICON = {
  laptop:  "\u{1F4BB}", // 💻
  ram:     "\u{1F9E0}", // 🧠
  ssd:     "\u{1F4BE}", // 💾
  battery: "\u{1F50B}", // 🔋
  adapter: "\u{1F50C}", // 🔌
  charger: "\u{1F50C}", // 🔌
};

// Strip filler words from the model to leave the compact identifier
// ("EliteBook 1040" → "1040", "Latitude 5420" → "5420", "ThinkPad T14" → "T14").
const _laptopModelToken = (model) => {
  if (!model) return "";
  const cleaned = String(model)
    .replace(/\b(EliteBook|ProBook|Notebook|Latitude|ThinkPad|Pavilion|Inspiron|IdeaPad|Vivobook|Zenbook|Envy|Spectre|OptiPlex|ThinkCentre|MacBook)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  // Prefer the last token if it contains a digit (typical model number),
  // otherwise join what's left.
  const last = parts[parts.length - 1] || "";
  return /\d/.test(last) ? last : parts.join("");
};

const _abbrev = (line) => {
  const it = (line.item_type || "laptop").toLowerCase();
  const brand = (line.brand || "").split(/\s+/)[0] || "";
  const model = line.model || "";
  if (it === "laptop") {
    const tok = _laptopModelToken(model);
    const ram = line.ram_gb != null ? `${line.ram_gb}` : "?";
    const storage = line.storage_gb != null && line.storage_gb > 0
      ? `${line.storage_gb}` : "NoSSD";
    return `${brand}${tok}-${ram}-${storage}`;
  }
  if (it === "ram") {
    const cap  = (model.match(/(\d+)\s*GB/i) || [])[1];
    const tech = (model.match(/DDR[2-9]/i) || [])[0];
    const freq = (model.match(/(\d{3,5})\s*MHz/i) || [])[1];
    const mod  = (model.match(/SO-DIMM|DIMM/i) || [])[0];
    return [brand || "RAM", tech, cap ? `${cap}GB` : null, freq, mod]
      .filter(Boolean).join(" ");
  }
  if (it === "ssd") {
    const capM = model.match(/(\d+(?:\.\d+)?)\s*(TB|GB)/i);
    const tech = (model.match(/NVMe\s*PCIe|NVMe|M\.2\s*SATA|mSATA|SATA/i) || [])[0];
    const form = (model.match(/2\.5"|M\.2\s*\d{4}|mSATA/i) || [])[0];
    return [brand || "SSD", tech, form, capM ? `${capM[1]}${capM[2].toUpperCase()}` : null]
      .filter(Boolean).join(" ");
  }
  if (it === "battery") {
    const wh = (model.match(/(\d{2,4})\s*Wh/i) || [])[1];
    return `${brand || ""} Battery${wh ? ` ${wh}Wh` : ""}`.trim();
  }
  if (it === "adapter" || it === "charger") {
    const w = (model.match(/(\d{2,4})\s*W\b/i) || [])[1];
    return `${brand || ""} Adapter${w ? ` ${w}W` : ""}`.trim();
  }
  return `${brand} ${model}`.trim() || "—";
};

const buildProductSummary = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  // Bucket by item_type + abbreviation so identical configs are combined.
  const buckets = new Map();
  lines.forEach((l) => {
    const it = (l.item_type || "laptop").toLowerCase();
    const key = `${it}||${_abbrev(l)}`;
    const qty = Number(l.quantity ?? 0) || 0;
    const prev = buckets.get(key) || { it, label: _abbrev(l), qty: 0 };
    prev.qty += qty;
    buckets.set(key, prev);
  });
  return Array.from(buckets.values()).map(({ it, label, qty }) => ({
    icon: ITEM_ICON[it] || "\u{1F4E6}", // 📦 fallback
    label,
    qty,
  }));
};

const WORKFLOW_LABEL = {
  PENDING_RECEIVING: "Pending Receiving",
  PENDING_QC: "Pending QC",
  QC_IN_PROGRESS: "QC In Progress",
  PENDING_BATTERY_TESTING: "Pending Battery Testing",
  COMPLETED: "Completed",
  DRAFT: "Draft",
};
const WORKFLOW_COLORS = {
  // Palette matches the requested color legend:
  //   yellow · Pending Receiving | orange · Pending QC | blue · QC In Progress
  //   purple · Pending Battery Testing | green · Completed
  PENDING_RECEIVING:        "bg-yellow-100 text-yellow-800  ring-1 ring-yellow-200",
  PENDING_QC:               "bg-orange-100 text-orange-800  ring-1 ring-orange-200",
  QC_IN_PROGRESS:           "bg-blue-100   text-blue-800    ring-1 ring-blue-200",
  PENDING_BATTERY_TESTING:  "bg-purple-100 text-purple-800  ring-1 ring-purple-200",
  COMPLETED:                "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  DRAFT:                    "bg-slate-200  text-slate-700",
};
const WORKFLOW_DOT = {
  PENDING_RECEIVING:        "bg-yellow-500",
  PENDING_QC:               "bg-orange-500",
  QC_IN_PROGRESS:           "bg-blue-500",
  PENDING_BATTERY_TESTING:  "bg-purple-500",
  COMPLETED:                "bg-emerald-500",
  DRAFT:                    "bg-slate-400",
};

// Derive the friendly workflow key from the raw DB status + unit-level
// progress counts. Falls back gracefully when progress is missing.
function workflowKey(dbStatus, progress) {
  const prog = progress || {};
  const qcDone = Number(prog.qc_done || 0);
  const total = Number(prog.total || 0);
  if (dbStatus === "DRAFT") return "DRAFT";
  if (dbStatus === "SUBMITTED" || dbStatus === "PENDING_RECEIVING") return "PENDING_RECEIVING";
  if (dbStatus === "PENDING_SERIAL_ENTRY" || dbStatus === "QC_PENDING") {
    return qcDone > 0 && qcDone < total ? "QC_IN_PROGRESS" : "PENDING_QC";
  }
  if (dbStatus === "PENDING_BATTERY_DETAILS") return "PENDING_BATTERY_TESTING";
  if (dbStatus === "READY_FOR_SALE" || dbStatus === "QC_COMPLETED") return "COMPLETED";
  return dbStatus;
}

// Kept for the Drafts tab which still surfaces the raw enum in its own column.
const STATUS_LABEL = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  PENDING_RECEIVING: "Pending Receiving",
  PENDING_SERIAL_ENTRY: "Pending Serial Entry",
  QC_PENDING: "Pending QC",
  PENDING_BATTERY_DETAILS: "Pending Battery Testing",
  READY_FOR_SALE: "Ready for Sale",
  QC_COMPLETED: "QC Completed",
};
const STATUS_COLORS = {
  DRAFT: "bg-slate-200 text-slate-700",
  SUBMITTED: "bg-amber-100 text-amber-800",
  PENDING_RECEIVING: "bg-amber-100 text-amber-800",
  PENDING_SERIAL_ENTRY: "bg-orange-100 text-orange-800",
  QC_PENDING: "bg-blue-100 text-blue-800",
  PENDING_BATTERY_DETAILS: "bg-indigo-100 text-indigo-800",
  READY_FOR_SALE: "bg-emerald-100 text-emerald-800",
  QC_COMPLETED: "bg-emerald-100 text-emerald-800",
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—");
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

export default function PurchasesPage() {
  const { user } = useAuth();
  const canCreate = canProcure(user);
  const canDelete = canDeleteRecords(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [status, setStatus] = useState("");
  const [tab, setTab] = useState("active");
  const [busyId, setBusyId] = useState(null);
  // V2.9.4 — Per-PO product-summary cache. Keys: purchase id. Values:
  // array of {icon, label, qty} or `"loading"` while a fetch is in flight,
  // or `null` when the PO has no lines. Populated by a lightweight fanout
  // (existing GET /api/purchases/{id}) triggered after each list load.
  const [summaries, setSummaries] = useState({});
  useEffect(() => { const t = setTimeout(() => setQDeb(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [tab]);

  const params = useMemo(() => {
    const p = { page, page_size: 20 };
    if (qDeb) p.q = qDeb;
    if (tab === "drafts") p.status = "DRAFT";
    else if (status) p.status = status;
    return p;
  }, [page, qDeb, status, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/purchases", { params })).data); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("PurchasesPage.load failed", err?.message || err);
      setData({ items: [], meta: null });
    }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  // V2.9.4 — Fan out one GET /api/purchases/{id} per visible PO to fetch
  // its lines, then bucket them into a compact product summary. Results
  // are cached across pages / re-renders. Fully non-blocking — the row
  // renders with a placeholder while the summary is loading.
  //
  // A ref tracks in-flight requests so StrictMode's double-invocation of
  // effects (dev mode) does not double-fetch, and so we can safely accept
  // async results without a cancel flag (which previously discarded them
  // and left rows stuck in "Loading contents…").
  const inflightRef = React.useRef(new Set());
  useEffect(() => {
    const items = (data?.items) || [];
    const missing = items.filter(
      (p) => summaries[p.id] === undefined && !inflightRef.current.has(p.id),
    );
    if (missing.length === 0) return;
    missing.forEach((p) => inflightRef.current.add(p.id));
    setSummaries((prev) => {
      const next = { ...prev };
      missing.forEach((p) => { next[p.id] = "loading"; });
      return next;
    });
    (async () => {
      const results = await Promise.allSettled(
        missing.map((p) => api.get(`/purchases/${p.id}`).then((r) => [p.id, r.data])),
      );
      setSummaries((prev) => {
        const next = { ...prev };
        results.forEach((res) => {
          if (res.status !== "fulfilled") return;
          const [pid, detail] = res.value;
          next[pid] = buildProductSummary(detail?.lines || []);
          inflightRef.current.delete(pid);
        });
        // Also drop any inflight entries that failed so they can be retried.
        results.forEach((res, i) => {
          if (res.status !== "fulfilled") inflightRef.current.delete(missing[i].id);
        });
        return next;
      });
    })();
  }, [data, summaries]);

  const goEdit = (id) => { window.location.href = `/admin/purchases/new?draftId=${id}`; };
  const goResume = (id) => { window.location.href = `/admin/purchases/new?draftId=${id}`; };

  const submitDraft = async (id) => {
    if (!window.confirm("Submit this draft? It will become a purchase and go to Warehouse for receiving.")) return;
    setBusyId(id);
    try {
      await api.post(`/purchases/${id}/submit`);
      await load();
      window.location.href = `/admin/purchases/${id}`;
    } catch (err) {
      alert("Submit failed: " + (err?.response?.data?.detail?.message || err?.message));
    } finally { setBusyId(null); }
  };

  const deleteDraft = async (id) => {
    // Spec §1 answer 5: soft-delete requires a mandatory reason recorded in audit_log.
    const reason = window.prompt("Reason for deleting this draft (mandatory, min 3 chars):", "");
    if (reason === null) return; // cancelled
    if (!reason || reason.trim().length < 3) {
      alert("A reason of at least 3 characters is required.");
      return;
    }
    if (!window.confirm(`Permanently delete this draft?\n\nReason: ${reason}\n\nThis cannot be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/purchases/${id}`, { data: { reason: reason.trim() } });
      await load();
    } catch (err) {
      alert("Delete failed: " + (err?.response?.data?.detail?.message || err?.message));
    } finally { setBusyId(null); }
  };

  const isDraftsTab = tab === "drafts";

  return (
    <div>
      <PageHeader
        title="Purchases"
        subtitle="Every laptop enters RentOPC OS through a purchase. Reception enters vendor + laptop configuration only; Warehouse and QC follow the workflow."
        actions={canCreate ? (
          <Link to="/admin/purchases/new" data-testid="btn-new-purchase"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm">
            <Plus size={14} /> New Purchase
          </Link>
        ) : null}
      />

      <div className="mb-3 flex items-center gap-2 border-b border-slate-200" data-testid="purchases-tabs">
        <button type="button" onClick={() => setTab("active")} data-testid="tab-active"
          className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === "active" ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Active Purchases
        </button>
        <button type="button" onClick={() => setTab("drafts")} data-testid="tab-drafts"
          className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === "drafts" ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Drafts
        </button>
      </div>

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search PO / invoice / vendor / serial / model…" />
        {!isDraftsTab && (
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="filter-status"
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option value="">All statuses</option>
            <option value="PENDING_RECEIVING">Pending Receiving</option>
            <option value="QC_PENDING">Pending QC / QC In Progress</option>
            <option value="PENDING_BATTERY_DETAILS">Pending Battery Testing</option>
            <option value="READY_FOR_SALE">Completed</option>
            <option value="SUBMITTED">Submitted (legacy)</option>
            <option value="PENDING_SERIAL_ENTRY">Pending Serial Entry (legacy)</option>
          </select>
        )}
      </Toolbar>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="purchases-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            {isDraftsTab ? (
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Last Modified</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            ) : (
              <tr>
                <th className="text-left px-3 py-2">Purchase ID</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-right px-3 py-2">Date</th>
                <th className="text-right px-3 py-2">Units</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-left px-3 py-2">Workflow Status</th>
                <th className="text-left px-3 py-2">Progress</th>
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isDraftsTab ? 7 : 8} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? data.items.map((p) => (
              isDraftsTab ? (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`draft-row-${p.id}`}>                  <td className="px-3 py-2">{p.vendor_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.invoice_no || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.total_units || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(p.total_amount)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDateTime(p.updated_at || p.created_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[p.status] || "bg-slate-100"}`}>{STATUS_LABEL[p.status] || p.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => goEdit(p.id)} disabled={busyId === p.id}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 px-2 py-1 text-xs"
                        data-testid={`draft-edit-${p.id}`}>
                        <Pencil size={12}/> Edit
                      </button>
                      <button type="button" onClick={() => goResume(p.id)} disabled={busyId === p.id}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 px-2 py-1 text-xs"
                        data-testid={`draft-resume-${p.id}`}>
                        <Play size={12}/> Resume
                      </button>
                      <button type="button" onClick={() => submitDraft(p.id)} disabled={busyId === p.id}
                        className="inline-flex items-center gap-1 rounded bg-slate-900 hover:bg-slate-800 text-white px-2 py-1 text-xs"
                        data-testid={`draft-submit-${p.id}`}>
                        {busyId === p.id ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>} Submit
                      </button>
                      {canDelete && (
                        <button type="button" onClick={() => deleteDraft(p.id)} disabled={busyId === p.id}
                          className="inline-flex items-center gap-1 rounded border border-rose-300 hover:bg-rose-50 text-rose-700 px-2 py-1 text-xs"
                          data-testid={`draft-delete-${p.id}`}>
                          <Trash2 size={12}/> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <PurchaseSummaryRows key={p.id} p={p} summary={summaries[p.id]} />
              )
            )) : (
              <tr><td colSpan={isDraftsTab ? 7 : 8}><EmptyState label={isDraftsTab ? "No drafts yet." : "No purchases yet."} /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />
    </div>
  );
}

// --- V1.7 helpers -----------------------------------------------------------

// V2.9.4 — Two-row PO renderer: main row + a compact grey summary caption row
// under it. Kept as its own component to avoid restructuring the map above.
function PurchaseSummaryRows({ p, summary }) {
  return (
    <React.Fragment>
      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
          onClick={() => { window.location.href = `/admin/purchases/${p.id}`; }}
          data-testid={`purchase-${p.id}`}>
        <td className="px-3 py-2">
          <Link to={`/admin/purchases/${p.id}`} onClick={(e) => e.stopPropagation()}
                className="font-mono font-medium text-slate-900 hover:underline">{p.purchase_id}</Link>
        </td>
        <td className="px-3 py-2">{p.vendor_name || "—"}</td>
        <td className="px-3 py-2 font-mono text-xs">{p.invoice_no || "—"}<div className="text-slate-400">{fmtDate(p.invoice_date)}</div></td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtDate(p.purchase_date)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{p.total_units}</td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(p.total_amount)}</td>
        <td className="px-3 py-2" data-testid={`purchase-workflow-status-${p.id}`}>
          <WorkflowStatusBadge dbStatus={p.status} progress={p.progress}/>
        </td>
        <td className="px-3 py-2" data-testid={`purchase-progress-${p.id}`}>
          <ProgressCell dbStatus={p.status} progress={p.progress} totalUnits={p.total_units}/>
        </td>
      </tr>
      <tr className="cursor-pointer hover:bg-slate-50"
          onClick={() => { window.location.href = `/admin/purchases/${p.id}`; }}
          data-testid={`purchase-summary-${p.id}`}>
        <td colSpan={8} className="px-3 pb-2 pt-0">
          <div className="text-[11px] text-slate-500 truncate">
            {summary === "loading" || summary === undefined ? (
              <span className="italic text-slate-400">Loading contents…</span>
            ) : !summary || summary.length === 0 ? (
              <span className="italic text-slate-400">No line items</span>
            ) : (
              summary.map((entry, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-slate-300">, </span>}
                  <span className="mr-1">{entry.icon}</span>
                  <span>{entry.label} ×{entry.qty}</span>
                </span>
              ))
            )}
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}

function WorkflowStatusBadge({ dbStatus, progress }) {
  const key = workflowKey(dbStatus, progress);
  const label = WORKFLOW_LABEL[key] || key;
  const color = WORKFLOW_COLORS[key] || "bg-slate-100 text-slate-700";
  const dot = WORKFLOW_DOT[key] || "bg-slate-400";
  return (
    <span
      data-workflow-key={key}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dot}`} aria-hidden="true"/>
      {label}
    </span>
  );
}

function ProgressCell({ dbStatus, progress, totalUnits }) {
  const total = Number(progress?.total ?? totalUnits ?? 0);
  const received = Number(progress?.received ?? 0);
  const qcDone = Number(progress?.qc_done ?? 0);
  const batteryDone = Number(progress?.battery_done ?? 0);
  if (dbStatus === "DRAFT" || total === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const Row = ({ label, n, tone }) => (
    <div className="flex items-center gap-1 text-xs">
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${tone}`} aria-hidden="true"/>
      <span className="text-slate-500 w-16">{label}</span>
      <span className="font-mono tabular-nums text-slate-800">{n}/{total}</span>
    </div>
  );
  return (
    <div className="space-y-0.5" data-testid="progress-cell">
      <Row label="Receive"  n={received}    tone="bg-yellow-500"/>
      <Row label="QC"       n={qcDone}      tone="bg-blue-500"/>
      <Row label="Battery"  n={batteryDone} tone="bg-purple-500"/>
    </div>
  );
}
